import React, { useState } from 'react';
import { QrCode, Printer, Plus, CheckCircle, AlertCircle, Trash2, FileDown, Square, CheckSquare } from 'lucide-react';
import api from '../../services/api';

export default function EstoqueGeradorQR() {
  const [quantidade, setQuantidade] = useState(10);
  const [gerando, setGerando] = useState(false);
  const [lote, setLote] = useState(null);
  const [erro, setErro] = useState('');
  const [selecionados, setSelecionados] = useState(new Set());
  const [deletando, setDeletando] = useState(false);
  const [baixandoPDF, setBaixandoPDF] = useState(false);

  async function gerar() {
    if (quantidade < 1 || quantidade > 200) { setErro('Quantidade deve ser entre 1 e 200'); return; }
    setGerando(true); setErro(''); setLote(null); setSelecionados(new Set());
    try {
      const { data } = await api.post('/estoque/qrcodes/gerar', { quantidade });
      setLote(data.data);
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao gerar QR codes');
    } finally { setGerando(false); }
  }

  function toggleSelecionado(id) {
    setSelecionados(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleTodos() {
    if (selecionados.size === lote.length) {
      setSelecionados(new Set());
    } else {
      setSelecionados(new Set(lote.map(r => r.id)));
    }
  }

  async function deletarSelecionados() {
    if (!selecionados.size) return;
    const confirmMsg = selecionados.size === 1
      ? 'Deletar este QR code? Esta ação não pode ser desfeita.'
      : `Deletar ${selecionados.size} QR codes selecionados? Esta ação não pode ser desfeita.`;
    if (!window.confirm(confirmMsg)) return;

    setDeletando(true);
    try {
      await Promise.all([...selecionados].map(id => api.delete(`/estoque/qrcodes/${id}`)));
      setLote(prev => prev.filter(r => !selecionados.has(r.id)));
      setSelecionados(new Set());
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao deletar QR codes');
    } finally { setDeletando(false); }
  }

  async function baixarPDF() {
    const ids = selecionados.size > 0 ? [...selecionados] : lote.map(r => r.id);
    if (!ids.length) return;
    setBaixandoPDF(true);
    try {
      const response = await api.post('/estoque/qrcodes/pdf', { ids }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-etiquetas-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErro('Erro ao gerar PDF. Tente novamente.');
    } finally { setBaixandoPDF(false); }
  }

  function imprimirLote() {
    const itens = lote.map(r => `
      <div class="qr-item">
        <img src="${r.qr_data_url}" class="qr-img" />
        <div class="numero">${r.numero_etiqueta}</div>
        <div class="logo">Inbase</div>
      </div>
    `).join('');

    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Etiquetas QR — Inbase Estoque</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0;}
      body{font-family:Arial,sans-serif;background:#fff;}
      .grid{display:flex;flex-wrap:wrap;gap:4mm;padding:8mm;}
      .qr-item{
        width:40mm;border:0.5pt solid #ccc;border-radius:2mm;
        padding:2mm;display:flex;flex-direction:column;align-items:center;
        page-break-inside:avoid;
      }
      .qr-img{width:34mm;height:34mm;}
      .numero{font-size:8pt;font-weight:bold;color:#1B3F5E;margin-top:1mm;letter-spacing:0.5px;}
      .logo{font-size:6pt;color:#999;margin-top:0.5mm;}
      @media print{
        @page{size:A4;margin:8mm;}
        body{margin:0;}
        .no-print{display:none!important;}
      }
    </style></head><body>
    <div class="no-print" style="padding:16px;background:#f0f4f8;display:flex;align-items:center;gap:12px;">
      <strong>Lote de ${lote.length} etiquetas QR — Inbase Estoque</strong>
      <button onclick="window.print()" style="padding:8px 16px;background:#1B3F5E;color:#fff;border:none;border-radius:8px;cursor:pointer;font-size:14px;">
        Imprimir
      </button>
      <button onclick="window.close()" style="padding:8px 16px;background:#e2e8f0;border:none;border-radius:8px;cursor:pointer;font-size:14px;">
        Fechar
      </button>
    </div>
    <div class="grid">${itens}</div>
    </body></html>`);
    w.document.close();
  }

  const todosSelec = lote && selecionados.size === lote.length;
  const algumSelec = selecionados.size > 0;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <QrCode size={24} className="text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Gerador de Etiquetas QR</h1>
          <p className="text-sm text-gray-500">Gere QR codes para impressão em metal antes de cadastrar as ferramentas</p>
        </div>
      </div>

      {/* Gerador */}
      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-base font-semibold text-gray-700 mb-4">Gerar novo lote</h2>
        <div className="flex items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Quantidade de etiquetas</label>
            <input
              type="number" min={1} max={200} value={quantidade}
              onChange={e => setQuantidade(parseInt(e.target.value) || 1)}
              className="w-36 border border-gray-200 rounded-xl px-3 py-2.5 text-sm font-medium text-center focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <p className="text-xs text-gray-400 mt-1">Máx. 200 por vez</p>
          </div>
          <button onClick={gerar} disabled={gerando}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
            <Plus size={18} />
            {gerando ? 'Gerando...' : `Gerar ${quantidade} QR codes`}
          </button>
        </div>
        {erro && (
          <div className="mt-3 flex items-center gap-2 text-red-600 bg-red-50 rounded-xl px-3 py-2 text-sm">
            <AlertCircle size={16} /> {erro}
          </div>
        )}
      </div>

      {/* Como funciona */}
      {!lote && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-blue-800">Como funciona o fluxo com etiquetas metálicas</h3>
          <ol className="space-y-2 text-sm text-blue-700">
            <li className="flex gap-2"><span className="font-bold text-blue-500 flex-shrink-0">1.</span> Gere o lote de QR codes aqui e salve o PDF para enviar à gráfica/metalúrgica</li>
            <li className="flex gap-2"><span className="font-bold text-blue-500 flex-shrink-0">2.</span> Rebie a placa de metal na ferramenta correspondente</li>
            <li className="flex gap-2"><span className="font-bold text-blue-500 flex-shrink-0">3.</span> Ao cadastrar a ferramenta, clique em "Escanear QR pré-impresso" e leia a placa com o celular</li>
            <li className="flex gap-2"><span className="font-bold text-blue-500 flex-shrink-0">4.</span> O QR code fica vinculado àquela ferramenta — ao escanear em campo, aparece o item correto</li>
          </ol>
        </div>
      )}

      {/* Lote gerado */}
      {lote && (
        <div className="space-y-4">
          {/* Barra de ações */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <CheckCircle size={20} className="text-green-500" />
              <span className="font-semibold text-gray-800">{lote.length} etiquetas geradas</span>
              <span className="text-sm text-gray-500">
                {lote[0]?.numero_etiqueta} → {lote[lote.length - 1]?.numero_etiqueta}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              {/* Selecionar todos */}
              <button onClick={toggleTodos}
                className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50">
                {todosSelec ? <CheckSquare size={15} /> : <Square size={15} />}
                {todosSelec ? 'Desmarcar todos' : 'Selecionar todos'}
              </button>

              {/* Deletar selecionados */}
              {algumSelec && (
                <button onClick={deletarSelecionados} disabled={deletando}
                  className="flex items-center gap-1.5 px-3 py-2 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm font-medium hover:bg-red-100 disabled:opacity-50">
                  <Trash2 size={15} />
                  {deletando ? 'Deletando...' : `Deletar ${selecionados.size} selecionado${selecionados.size > 1 ? 's' : ''}`}
                </button>
              )}

              {/* Baixar PDF */}
              <button onClick={baixarPDF} disabled={baixandoPDF}
                className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 disabled:opacity-50">
                <FileDown size={15} />
                {baixandoPDF
                  ? 'Gerando PDF...'
                  : algumSelec
                    ? `PDF (${selecionados.size} selecionado${selecionados.size > 1 ? 's' : ''})`
                    : `PDF (${lote.length} etiquetas)`}
              </button>

              {/* Imprimir */}
              <button onClick={imprimirLote}
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-800 text-white rounded-xl text-sm font-semibold hover:bg-gray-900">
                <Printer size={15} /> Imprimir
              </button>

              {/* Novo lote */}
              <button onClick={() => { setLote(null); setQuantidade(10); setSelecionados(new Set()); }}
                className="px-3 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50">
                Gerar outro lote
              </button>
            </div>
          </div>

          {/* Grade de preview */}
          <div className="bg-white rounded-xl shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-gray-400">{lote.length} etiquetas — clique para selecionar. PDF gerado em alta resolução (1000px) para gravação em metal.</p>
              {algumSelec && (
                <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                  {selecionados.size} selecionado{selecionados.size > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              {lote.map(r => {
                const sel = selecionados.has(r.id);
                return (
                  <div
                    key={r.id}
                    onClick={() => toggleSelecionado(r.id)}
                    className={`relative flex flex-col items-center border-2 rounded-lg p-2 w-28 cursor-pointer transition-all select-none
                      ${sel
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'}`}
                  >
                    {sel && (
                      <div className="absolute top-1 right-1 bg-blue-500 rounded-full p-0.5">
                        <CheckSquare size={12} className="text-white" />
                      </div>
                    )}
                    <img src={r.qr_data_url} alt={r.numero_etiqueta} className="w-20 h-20" />
                    <span className="text-xs font-bold text-blue-800 mt-1">{r.numero_etiqueta}</span>
                    <span className="text-xs text-gray-400">Inbase</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-800">
            <strong>PDF para placas de metal:</strong> O arquivo PDF é gerado com QR codes em 1000px de resolução —
            adequado para gravação a laser em metal. Selecione apenas os QR desejados antes de baixar, ou baixe todos de uma vez.
          </div>
        </div>
      )}
    </div>
  );
}
